import React, { useEffect, FC, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { useMediaQuery } from "react-responsive";
import { useDebounce } from "use-debounce";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getClonesAsync, ITEMS_PER_PAGE } from "src/redux/clones/actions";
import { selectorClones } from "src/redux/clones/selector";
import { getFiltersValuesAsync } from "src/redux/filters/actions";
import { selectFiltersValues } from "src/redux/filters/selectors";
import { InquirySuggestionBanner } from "src/page-components/clones/InquirySuggestionBanner";
import {
  clonesTrns,
  noClonesBannerTrns,
} from "src/page-components/clones/trns";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { Filtering } from "src/components/Filtering";
import { Loader } from "src/components/Loader";
import { SortingDropdownList } from "src/components/SortingDropdownList";
import { SORTING_OPTIONS } from "src/components/SortingDropdownList/constants";
import { CloneCard } from "src/components/CloneCard";
import { Pagination } from "src/components/Pagination";
import { NoItemsBanner } from "src/components/NoItemsBanner";
import { MobFilteringMenu } from "src/components/Filtering/MobFilteringMenu";
import { FilteringButton } from "src/components/Filtering/FilteringButton";
import { BreadCrumbs } from "src/components/BreadCrumbs";
import { breadCrumbsTrns } from "src/components/BreadCrumbs/trns";
import { IBreadCrumbsItem } from "src/components/BreadCrumbs/types";
import { scrollTop } from "src/utils/scrollTop";
import { QUERY_PARAM_KEYS } from "src/constants/queryParams";
import { PATHNAMES } from "src/constants/routes";
import { SCREEN_BREAKPOINTS } from "src/constants/screenBreakpoints";
import { ISelectOption } from "src/@types/form";
import { Sizes } from "src/@types/sizes";
import { ONLY_FOR } from "src/components/PrivateRoute/types";
import { Show } from "src/components/PrivateRoute/Show";
import { ButtonVariants } from "src/components/Button/types";
import { ReactComponent as PlusIcon } from "src/assets/icons/plus.svg";
import { Link } from "src/components/Link";
import { Button } from "src/components/Button";
import { MetadataWrapper } from "src/components/MetadataWrapper";

const DEBOUNCE_DELAY_DESKTOP = 1000;
const DEBOUNCE_DELAY_MOBILE = 2500;

const Clones: FC = () => {
  const { t } = useTranslation();

  const [isOpenFiltersMenu, setIsOpenFiltersMenu] = useState(false);

  const isGLScreenBreakpoint = useMediaQuery({
    maxWidth: SCREEN_BREAKPOINTS.GL,
  });
  const debounceDelay = isGLScreenBreakpoint
    ? DEBOUNCE_DELAY_MOBILE
    : DEBOUNCE_DELAY_DESKTOP;

  const dispatch = useAppDispatch();

  const { queryParams, getQueryParam, setQueryParam, setMultipleQueryParams } =
    useQueryParams();

  const [debouncedQueryParams] = useDebounce(queryParams, debounceDelay);

  const filtersValues = useAppSelector(selectFiltersValues);

  useEffect(() => {
    dispatch(getFiltersValuesAsync());
  }, [dispatch]);

  const activeOption = SORTING_OPTIONS.find(
    ({ value }) => value === getQueryParam(QUERY_PARAM_KEYS.SORTING)
  );

  const setOption = ({ value }: ISelectOption) =>
    setMultipleQueryParams({
      [QUERY_PARAM_KEYS.SORTING]: value,
      [QUERY_PARAM_KEYS.PAGE]: 1,
    });

  const currentPage = Number(getQueryParam(QUERY_PARAM_KEYS.PAGE)) || 1;

  const setPage = (page: number) => {
    scrollTop();
    if (page !== currentPage) {
      setQueryParam(QUERY_PARAM_KEYS.PAGE, page.toString());
    }
  };

  const filteredQueryParams = useMemo(() => {
    const params = new URLSearchParams(debouncedQueryParams);
    params.delete(QUERY_PARAM_KEYS.IS_SHOWED_MORE);
    return params.toString();
  }, [debouncedQueryParams]);

  useEffect(() => {
    dispatch(getClonesAsync({ filters: filteredQueryParams }));
  }, [filteredQueryParams]);

  const clonesResponse = useAppSelector(selectorClones);
  const { isLoading, data } = clonesResponse || {};

  const clonesResults = data?.results || [];

  const pageCount = Math.ceil(data?.count / ITEMS_PER_PAGE);
  const isPaginated = pageCount > 1;

  const breadCrumbsItems: IBreadCrumbsItem[] = [
    { title: { i18nKey: breadCrumbsTrns.home }, path: PATHNAMES.HOME },
    { title: { i18nKey: breadCrumbsTrns.clones } },
  ];

  return (
    <MetadataWrapper name="clones">
      <PageWrapper mainClassName="pt-10 pb-25 mt-19">
        <section className="container flex flex-col gap-3 gl:gap-10">
          {!isGLScreenBreakpoint && <BreadCrumbs items={breadCrumbsItems} />}

          <h1 className="font-semibold">{t(clonesTrns.title)}</h1>

          <div className="flex items-start justify-between gap-3">
            {!isGLScreenBreakpoint && (
              <Filtering
                filtersValues={filtersValues?.clone}
                pagePath={PATHNAMES.CLONES}
              />
            )}

            {isLoading ? (
              <Loader size={Sizes.L} className="self-center" />
            ) : (
              <div className="flex flex-1 flex-col gap-3">
                {isGLScreenBreakpoint && (
                  <>
                    <FilteringButton
                      setIsOpen={setIsOpenFiltersMenu}
                      className="self-start"
                    />

                    <MobFilteringMenu
                      isOpen={isOpenFiltersMenu}
                      setIsOpen={setIsOpenFiltersMenu}
                      filtersValues={filtersValues?.clone}
                      pagePath={PATHNAMES.CLONES}
                    />
                  </>
                )}
                <div className="flex items-center justify-between">
                  <SortingDropdownList
                    options={SORTING_OPTIONS}
                    activeOption={activeOption || SORTING_OPTIONS[0]}
                    setOption={setOption}
                  />
                  <Show onlyFor={ONLY_FOR.AUTHORIZED} showLoader={false}>
                    <Link href={PATHNAMES.CREATE_CLONE}>
                      <Button
                        variant={ButtonVariants.SECONDARY}
                        className="flex items-center justify-center lg:px-5 py-2"
                      >
                        <PlusIcon />
                        <span>{t(clonesTrns.addCloneButton)}</span>
                      </Button>
                    </Link>
                  </Show>
                </div>

                {data?.count > 0 ? (
                  <div className="flex flex-col items-center gap-3">
                    <div
                      className={cn("grid grid-cols-1 gap-3", {
                        "sm:grid-cols-2 md:grid-cols-1":
                          clonesResults?.length > 1,
                      })}
                    >
                      {Boolean(clonesResults?.length) &&
                        clonesResults.map((clone) => (
                          <CloneCard key={clone.id} clone={clone} />
                        ))}
                    </div>
                    {isPaginated && (
                      <Pagination
                        className="self-center my-8"
                        page={currentPage}
                        pageCount={pageCount}
                        setPage={setPage}
                      />
                    )}

                    <InquirySuggestionBanner />
                  </div>
                ) : (
                  <NoItemsBanner
                    title={t(noClonesBannerTrns.title)}
                    description={t(noClonesBannerTrns.description)}
                    href={PATHNAMES.CREATE_INQUIRY}
                    textButton={t(noClonesBannerTrns.title)}
                    onlyFor={ONLY_FOR.AGENT}
                  />
                )}
              </div>
            )}
          </div>
        </section>
      </PageWrapper>
    </MetadataWrapper>
  );
};

export default Clones;
